/**
 * 투자분석/자금계획 공유 mixin (Options API).
 * 호스트 SFC 필수 속성: this.item, this.estimate, this.showEstimate, this.$route.
 * 선택: this.estimateAvm(이번에 받은 블록 값), this.estimateAboveSell(). 추정가 저장 판정에 쓴다.
 * SFC 옵션 invPagesDefaultOff:true 면 ③④ 기본 OFF(임대 변형).
 * SFC 옵션 printBuildingType:'sell'|'rent' 로 출력 기본값 저장 대상 구분.
 */
import {
	buildDefaults, computeAcquisition, computeDevelopment, computeEstimateBadge,
	computeFundingComparison, serializeInvestment, applyInvestmentQuery,
	isResidential, ALL_QUERY_KEYS, estimateSettings,
} from '@/utils/investment.mjs';
import {
	serializeSlides, slidesFromQueryValue, makeBlankSlide, makePhotoSlide,
} from '@/utils/printSlides.mjs';

export default {
	data() {
		const off = !!this.$options.invPagesDefaultOff;
		// 리모델링/신축(devShow)은 매각·임대 공통으로 항상 기본 OFF(선택 제외).
		// 투자분석(invShow)만 임대 변형(invPagesDefaultOff)에 따라 기본값이 갈린다.
		return { invState: null, invShow: !off, devShow: false, defaultsStatus: '', slides: [], slideUploading: false };
	},
	computed: {
		invAcq() { return this.invState ? computeAcquisition(this.invState) : null; },
		invDev() { return this.invState ? computeDevelopment(this.invState) : null; },
		invFunding() { return (this.invAcq && this.invDev) ? computeFundingComparison(this.invAcq, this.invDev) : null; },
		invResidential() { return this.item ? isResidential(this.item.main_purpose) : false; },
		// 담당자(building_mem_uid===로그인 mem_uid)만 저장 버튼 노출. 서버측 403 게이트와 이중.
		isManager() {
			const me = (this.$store && this.$store.getters) ? this.$store.getters.getUserInfo : null;
			return !!(me && this.item && this.item.building_mem_uid != null
				&& String(me.mem_uid) === String(this.item.building_mem_uid));
		},
		estimateBadge() {
			if (!this.showEstimate || !this.estimate || !this.estimate.available || !this.invState) return null;
			return computeEstimateBadge(this.invState.sellMan, this.estimate.total);
		},
		estimateNote: {
			get() { return this.invState ? this.invState.note : ''; },
			set(v) { if (this.invState) { this.invState.note = v; this.syncInvestmentToUrl(); } },
		},
	},
	watch: {
		item() { this.ensureInvestmentState(); },
	},
	created() {
		this.ensureInvestmentState();
	},
	methods: {
		// DB 저장 기본값(item.printDefaults) 을 기준층으로, URL 쿼리를 우선으로 병합.
		// 타직원의 임시 URL 수정이 담당자 저장값을 덮되, DB 는 불변.
		mergedQuery() {
			const db = (this.item && this.item.printDefaults && typeof this.item.printDefaults === 'object')
				? this.item.printDefaults : {};
			const url = (this.$route && this.$route.query) ? this.$route.query : {};
			return { ...db, ...url };   // DB=기준, URL=우선
		},
		ensureInvestmentState() {
			if (this.invState || !this.item) return;
			const merged = this.mergedQuery();
			const base = buildDefaults(this.item);
			applyInvestmentQuery(base, merged);
			this.invState = base;
			if (merged.inv_show != null) this.invShow = String(merged.inv_show) !== '0';
			if (merged.dev_show != null) this.devShow = String(merged.dev_show) !== '0';
			// #7 부록 슬라이드: buildDefaults→DB→URL 병합값에서 로드(URL 우선). DB=item.printDefaults.slides.
			this.slides = slidesFromQueryValue(merged.slides);
		},
		syncInvestmentToUrl() {
			if (!this.invState || !this.$route) return;
			const u = new URL(window.location.href);
			const q = serializeInvestment(this.invState);
			ALL_QUERY_KEYS.forEach(k => u.searchParams.delete(k));
			Object.entries(q).forEach(([k, v]) => u.searchParams.set(k, v));
			u.searchParams.set('inv_show', this.invShow ? '1' : '0');
			u.searchParams.set('dev_show', this.devShow ? '1' : '0');
			// #7 슬라이드: 빈 배열도 '[]' 로 명시 — 키 누락 시 서버 PDF(=URL 로드)가 저장된 DB 기본값으로
			// 되돌아가 "세션에서 전부 지움" 이 무시되는 것 방지(serializeSlides 는 빈 상태에 null 반환).
			const sv = serializeSlides(this.slides);
			u.searchParams.set('slides', sv == null ? '[]' : sv);
			window.history.replaceState(window.history.state, '', u.pathname + u.search + u.hash);
		},
		// 현재 화면 편집값(노브+토글+추정가)을 저장 settings 평면객체로 직렬화.
		buildPrintSettings() {
			const s = {
				...serializeInvestment(this.invState),
				inv_show: this.invShow ? '1' : '0',
				dev_show: this.devShow ? '1' : '0',
			};
			// 추정가 키는 담당자가 고친 것만 싣는다(estimateSettings). 추정가가 없는 화면(임대)은 빈 객체.
			const showDefault = typeof this.estimateAboveSell === 'function' ? this.estimateAboveSell() : this.showEstimate;
			const saved = this.item && this.item.printDefaults && typeof this.item.printDefaults === 'object' ? this.item.printDefaults : null;
			Object.assign(s, estimateSettings(this.estimate, this.estimateAvm, this.showEstimate, showDefault, saved));
			// #7 슬라이드(있을 때만 저장; 비면 키 생략 → 저장 시 DB 기본값 슬라이드 초기화).
			const sv = serializeSlides(this.slides);
			if (sv) s.slides = sv;
			return s;
		},
		saveInvestmentDefaults() {
			if (!this.isManager || !this.item) return;
			const type = this.$options.printBuildingType === 'rent' ? 'rent' : 'sell';
			this.defaultsStatus = 'saving';
			this.$apiPOST('/admin/api/detail/item/print-defaults',
				{ type, bid: this.bid, settings: this.buildPrintSettings() })
				.then(re => { this.defaultsStatus = (re && re.st) ? 'saved' : 'error'; })
				.catch(() => { this.defaultsStatus = 'error'; });
		},
		resetInvestmentDefaults() {
			if (!this.isManager || !this.item) return;
			if (!window.confirm('이 물건의 저장된 출력 기본값을 초기화할까요? (현재 화면값은 유지)')) return;
			const type = this.$options.printBuildingType === 'rent' ? 'rent' : 'sell';
			this.defaultsStatus = 'saving';
			this.$apiPOST('/admin/api/detail/item/print-defaults', { type, bid: this.bid, settings: {} })
				.then(re => { this.defaultsStatus = (re && re.st) ? 'saved' : 'error'; })
				.catch(() => { this.defaultsStatus = 'error'; });
		},
		onInvestmentEdit() { this.syncInvestmentToUrl(); },
		onInvestmentToggle() { this.syncInvestmentToUrl(); },
		// #7 부록 슬라이드 ──────────────────────────────────────────────
		onSlideChange() { this.syncInvestmentToUrl(); },
		addBlankSlide() {
			if (this.slides.length >= 20) return;
			this.slides.push(makeBlankSlide(this.slides));
			this.syncInvestmentToUrl();
		},
		// 사진 1장 업로드 → building_print_slide_img 행 → imgId 로 photo 슬라이드 추가.
		// 서버 PDF(Puppeteer)는 클라 Blob 을 못 보므로 반드시 서버(R2) 영속화 후 id 참조.
		async addPhotoSlide(file) {
			if (!file || this.slideUploading || this.slides.length >= 20) return;
			const type = this.$options.printBuildingType === 'rent' ? 'rent' : 'sell';
			this.slideUploading = true;
			try {
				const fd = new FormData();
				fd.append('image', file);
				fd.append('bid', this.bid);
				fd.append('type', type);
				const token = (this.$store.getters.getToken || {}).accessToken || '';
				const res = await fetch(process.env.VUE_APP_HOST_BACK + '/admin/api/detail/item/print-slide/upload', {
					method: 'POST', headers: { Authorization: token }, body: fd,
				});
				const j = await res.json().catch(() => null);
				if (j && j.st && j.id) {
					this.slides.push(makePhotoSlide(this.slides, j.id, ''));
					this.syncInvestmentToUrl();
				} else {
					window.alert('사진 업로드 실패' + (j && j.reason ? ' (' + j.reason + ')' : ''));
				}
			} catch (e) {
				window.alert('사진 업로드 실패');
			} finally {
				this.slideUploading = false;
			}
		},
	},
};
